import { useTranslation } from "react-i18next";
import { FiCheck } from "react-icons/fi";
import { BusinessCreditsDTO } from "../../../../Common/Types-DTOs/ClientUser";
import { planNameKey } from "../../../../Common/Utils/dueDiligencePlans";
import { Button } from "../../../../Components/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  ViewAllLink,
  CreditsGrid,
  CreditBalance,
  CreditBalanceLabel,
  CreditsCard,
  BuyCreditsLink,
  PlanBadge,
  PlanName,
  RenewalInfo,
  FeaturesSection,
  FeatureItem,
  FeatureIcon,
  CardDescription,
} from "./Styles";

interface CreditsOverviewProps {
  credits: BusinessCreditsDTO;
  onOpenHistory: () => void;
  onSubscribe: () => void;
  /** Credits are only sold on top of a paid plan: offered only with one. */
  onBuyCredits: () => void;
  /** Leaves for the plans page, where a bigger plan can be picked. */
  onChangePlan: () => void;
}

/**
 * The paid DD plan or the way to get one, and beside it the credit balance
 * with the way into its history and, with a plan, to buy more.
 */
export const CreditsOverview = ({
  credits,
  onOpenHistory,
  onSubscribe,
  onBuyCredits,
  onChangePlan,
}: CreditsOverviewProps) => {
  const { t, i18n } = useTranslation();
  const plan = credits.dueDiligencePlan;

  return (
    <CreditsGrid>
      {plan && (
        <Card>
          <CardHeader style={{ marginBottom: 0 }}>
            <PlanBadge>{t("pricing.subscriptionContent.dueDiligencePlan.badge")}</PlanBadge>
            <ViewAllLink onClick={onChangePlan}>
              {t("pricing.subscriptionContent.dueDiligencePlan.changePlanLink")}
            </ViewAllLink>
          </CardHeader>
          <PlanName>{t(planNameKey(plan.lookupKey))}</PlanName>
          <RenewalInfo>
            {t("pricing.subscriptionContent.dueDiligencePlan.renewalInfo", {
              date: new Date(plan.paidUntil).toLocaleDateString(i18n.language, {
                day: "numeric",
                month: "long",
                year: "numeric",
              }),
            })}
          </RenewalInfo>
          {(plan.monthlyCredits !== null || plan.includesChat) && (
            <FeaturesSection style={{ marginTop: "1rem" }}>
              {plan.monthlyCredits !== null && (
                <FeatureItem>
                  <FeatureIcon>
                    <FiCheck />
                  </FeatureIcon>
                  <span>
                    {t("pricing.subscriptionContent.dueDiligencePlan.monthlyCredits", {
                      count: plan.monthlyCredits,
                    })}
                  </span>
                </FeatureItem>
              )}
              {plan.includesChat && (
                <FeatureItem>
                  <FeatureIcon>
                    <FiCheck />
                  </FeatureIcon>
                  <span>{t("pricing.subscriptionContent.dueDiligencePlan.includesChat")}</span>
                </FeatureItem>
              )}
            </FeaturesSection>
          )}
        </Card>
      )}

      {!plan && (
        <Card>
          <PlanBadge>{t("pricing.subscriptionContent.dueDiligencePlan.badge")}</PlanBadge>
          <PlanName>{t("pricing.subscriptionContent.dueDiligencePlan.emptyTitle")}</PlanName>
          <CardDescription style={{ marginTop: "0.25rem", marginBottom: "1rem" }}>
            {t("pricing.subscriptionContent.dueDiligencePlan.emptyDescription")}
          </CardDescription>
          <Button onClick={onSubscribe}>{t("pricing.subscriptionContent.dueDiligencePlan.subscribeButton")}</Button>
        </Card>
      )}

      <CreditsCard>
        <CardHeader>
          <CardTitle>{t("pricing.subscriptionContent.credits.cardTitle")}</CardTitle>
          <ViewAllLink onClick={onOpenHistory}>
            {t("pricing.subscriptionContent.credits.historyLink")}
          </ViewAllLink>
        </CardHeader>
        <CreditBalance>{credits.balance.toLocaleString(i18n.language)}</CreditBalance>
        <CreditBalanceLabel>
          {t("pricing.subscriptionContent.credits.balanceLabel", { count: credits.balance })}
        </CreditBalanceLabel>
        {plan && (
          <BuyCreditsLink onClick={onBuyCredits}>{t("pricing.subscriptionContent.credits.buyLink")}</BuyCreditsLink>
        )}
      </CreditsCard>
    </CreditsGrid>
  );
};
